import { appConfig } from "@/config";
import i18n from "@/core/i18n";
import { ApiErrorCode, apiErrorBodySchema, type ApiErrorBody } from "@repo/schema";
import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import { ACCESS_TOKEN_KEY } from "./constants";
import { refreshSession } from "./baseQuery";

/**
 * POST a request and read its NDJSON reply line by line.
 *
 * RTK Query cannot do this: `fetchBaseQuery` awaits the whole body before it
 * resolves, which is exactly the behaviour a streamed auto-annotation run
 * exists to avoid. So this is a plain `fetch`, deliberately wearing the same
 * clothes as `baseQuery` — the bearer token out of sessionStorage, credentials
 * included for the refresh cookie, the same base URL — and reusing
 * `refreshSession` rather than growing a second copy of the 401 dance.
 *
 * Errors come back in two shapes, and callers have to handle both because the
 * server cannot choose. Before the first byte a failure is still an ordinary
 * HTTP status and is thrown as `StreamHttpError`. After it, the status line has
 * already gone out as 200 and the failure arrives as an `error` event in the
 * stream itself — see `stream-ndjson.ts` on the API side.
 */

/** A failure the server could still answer with a status code. */
export class StreamHttpError extends Error {
  constructor(
    public readonly status: number,
    /** The API's error body (`code` is UNKNOWN when the reply was not one). */
    public readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = "StreamHttpError";
  }
}

/** Just enough of the RTK Query api object for `refreshSession`. */
export interface StreamAuthContext {
  dispatch: BaseQueryApi["dispatch"];
  getState: BaseQueryApi["getState"];
}

export interface StreamNdjsonArgs {
  url: string;
  body: unknown;
  signal: AbortSignal;
  auth: StreamAuthContext;
}

/**
 * Base URL + path, with exactly one slash between them.
 *
 * `fetchBaseQuery` does this for every other request in the app, which is why
 * the endpoint builders in `config/studioApi/endpoints.ts` all return bare
 * paths with no leading slash. Concatenating them directly produced
 * `http://localhost:3000/v1predict/...` — the base ends `/v1` and nothing
 * supplied the separator.
 */
const joinUrl = (base: string, path: string): string =>
  `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

const post = (args: StreamNdjsonArgs): Promise<Response> => {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  return fetch(joinUrl(appConfig.studioApi.baseUrl, args.url), {
    method: "POST",
    credentials: "include",
    signal: args.signal,
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": i18n.language,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(args.body),
  });
};

/** The API's error body, or a usable fallback when it is not JSON. */
const errorFrom = async (response: Response): Promise<StreamHttpError> => {
  let message = response.statusText || "Request failed";
  try {
    const raw: unknown = await response.json();
    const parsed = apiErrorBodySchema.safeParse(raw);
    if (parsed.success) return new StreamHttpError(response.status, parsed.data);
    const body = raw as { message?: unknown };
    if (typeof body.message === "string") message = body.message;
    else if (Array.isArray(body.message) && typeof body.message[0] === "string") {
      message = body.message[0];
    }
  } catch {
    // A non-JSON error body (a gateway's own HTML, say) leaves the status text.
  }
  return new StreamHttpError(response.status, {
    statusCode: response.status,
    code: ApiErrorCode.UNKNOWN,
    message,
  });
};

/**
 * Yield one parsed object per line, as they arrive.
 *
 * `T` is not validated here. The only producer is our own API, the events are
 * typed by `@repo/schema`, and running a zod parse per box on the render path
 * would cost more than it protects.
 */
export async function* streamNdjson<T>(
  args: StreamNdjsonArgs,
): AsyncGenerator<T> {
  let response = await post(args);

  // One retry, mirroring baseRefreshingQuery: an access token that expired
  // while the labeller was reading must not cost them the run.
  if (response.status === 401) {
    const refreshed = await refreshSession(args.auth as BaseQueryApi);
    if (!refreshed) throw await errorFrom(response);
    response = await post(args);
  }

  if (!response.ok || !response.body) throw await errorFrom(response);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // `stream: true` matters: a chunk boundary can land inside a multi-byte
      // character, and a thought summary is prose that will eventually contain
      // one.
      buffer += decoder.decode(value, { stream: true });

      let newline = buffer.indexOf("\n");
      while (newline !== -1) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (line) yield JSON.parse(line) as T;
        newline = buffer.indexOf("\n");
      }
    }
    // A server that ended without a trailing newline still owes us its last
    // event — in practice the `done` one.
    const tail = buffer.trim();
    if (tail) yield JSON.parse(tail) as T;
  } finally {
    // Aborting mid-run leaves the reader holding the connection; releasing it
    // here is what lets the browser tear the request down. Guarded because the
    // release races the abort that caused it: a reader whose pending read has
    // already been rejected throws instead of releasing, and that must not
    // become the error the caller sees in place of the real one.
    try {
      reader.releaseLock();
    } catch {
      /* already torn down */
    }
  }
}
